import { createContext, useContext, useEffect, useState } from "react";
import type { EventEnvelope } from "@snowiespace/protocol";

export type SpaceEventStatus = "idle" | "connecting" | "live" | "reconnecting";
export interface SpaceEventProjection {
  events: EventEnvelope[];
  status: SpaceEventStatus;
}

export const SpaceEventStatusContext = createContext<SpaceEventStatus>("idle");
export const SpaceEventsContext = createContext<SpaceEventProjection>({ events: [], status: "idle" });
export function useSpaceEventStatus(): SpaceEventStatus { return useContext(SpaceEventStatusContext); }
export function useSpaceEventProjection(): SpaceEventProjection { return useContext(SpaceEventsContext); }

export function useSpaceEvents(spaceId: string | undefined) {
  const [events, setEvents] = useState<EventEnvelope[]>([]);
  const [status, setStatus] = useState<SpaceEventStatus>("idle");

  useEffect(() => {
    setEvents([]);
    if (!spaceId) {
      setStatus("idle");
      return;
    }
	const activeSpaceId = spaceId;
    const controller = new AbortController();
    let cursor = Number(sessionStorage.getItem(`snowiespace.event.${activeSpaceId}`) ?? "0");
    let retry: ReturnType<typeof setTimeout> | undefined;
    function accept(incoming: EventEnvelope[]) {
      if (!incoming.length) return;
      cursor = incoming.at(-1)?.sequence ?? cursor;
      sessionStorage.setItem(`snowiespace.event.${activeSpaceId}`, String(cursor));
      setEvents((current) => {
        const bySequence = new Map(current.map((item) => [item.sequence, item]));
        for (const event of incoming) bySequence.set(event.sequence, event);
        return [...bySequence.values()].sort((left, right) => left.sequence - right.sequence).slice(-50);
      });
    }
    async function hydrate() {
      for (;;) {
        const response = await fetch(`/api/v2/spaces/${encodeURIComponent(activeSpaceId)}/events/snapshot?after=${cursor}`, { credentials: "include", headers: { Accept: "application/json" }, signal: controller.signal });
        if (!response.ok) throw new Error(`event hydration returned ${response.status}`);
        const page = await response.json() as { highWaterMark: number; events: EventEnvelope[] };
        accept(page.events);
        if (cursor >= page.highWaterMark || page.events.length === 0) {
          cursor = Math.max(cursor, page.highWaterMark);
          sessionStorage.setItem(`snowiespace.event.${activeSpaceId}`, String(cursor));
          return;
        }
      }
    }
    async function connect() {
      try {
        setStatus(cursor > 0 ? "reconnecting" : "connecting");
        await hydrate();
        const response = await fetch(`/api/v2/spaces/${encodeURIComponent(activeSpaceId)}/events?after=${cursor}`, { credentials: "include", headers: { Accept: "text/event-stream" }, signal: controller.signal });
        if (!response.ok || !response.body) throw new Error(`event stream returned ${response.status}`);
        setStatus("live");
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!controller.signal.aborted) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const frames = buffer.split("\n\n");
          buffer = frames.pop() ?? "";
          for (const frame of frames) {
            const data = frame.split("\n").find((line) => line.startsWith("data: "))?.slice(6);
            if (!data) continue;
            const event = JSON.parse(data) as EventEnvelope;
            accept([event]);
          }
        }
        if (!controller.signal.aborted) throw new Error("event stream ended");
      } catch {
        if (!controller.signal.aborted) {
          setStatus("reconnecting");
          retry = setTimeout(() => void connect(), 1_000);
        }
      }
    }
    void connect();
    return () => {
      controller.abort();
      if (retry) clearTimeout(retry);
    };
  }, [spaceId]);
  return { events, status };
}
